import type { Maybe, MaybeUndef } from '@@types/helpers'
import type { EntityCloudStatistics } from '@app/entities'
import type { AppRouteName } from '@app/routes'
import type { StoreRoot } from '@app/stores'
import { isDefined } from '@libs/isDefined'
import type { AppRouterClient } from '@libs/Router/types'

/**
 * Return true if the telemetry is disable on `routeName`.
 */
export function isTelemetryDisableOnRoute(appRouter: AppRouterClient) {
  return (routeName: Maybe<AppRouteName>) => {
    return (
      isDefined(routeName) &&
      appRouter.getRouteSpecs(routeName).noTelemetry === true
    )
  }
}

/**
 * Return true if the browser is Safari
 */
export function isSafariBrowser() {
  return (
    /constructor/i.test((window as any).HTMLElement) ||
    navigator.userAgent.indexOf('Safari') !== -1
  )
}

/**
 *
 * @returns user screen size: width in px x height in px (ex: "1920px x 1080px")
 */
export function getUserScreenSize() {
  return `${screen.width}px x ${screen.height}px`
}

/**
 *
 * @returns LicenseType: PROD, POV, INTERNAL, NFR
 */
export function getTieLicenseType(storeRoot: StoreRoot) {
  return storeRoot.stores.storeLicense.licenseType
}

/**
 *
 * @returns If user has Tenable session, will return its userUuid.
 * If user only has TIE License, will return containerUuid_tieUserId to have the same granularity level
 */
export function getUserTelemetryId(
  storeRoot: StoreRoot,
  cloudStatistics: Maybe<EntityCloudStatistics>
) {
  const canAccessTenableOne =
    storeRoot.stores.storeAbout.productAssociation?.isAssociatedToTenableOne()

  if (
    canAccessTenableOne &&
    storeRoot.stores.storeAuthentication.tulSession.uuid
  ) {
    return storeRoot.stores.storeAuthentication.tulSession.uuid
  }
  if (
    cloudStatistics?.containerUuid &&
    storeRoot.stores.storeAuthentication.whoAmI?.id
  ) {
    return `${cloudStatistics.containerUuid.toLowerCase()}_${storeRoot.stores.storeAuthentication.whoAmI.id}`
  }

  return null
}

/**
 *
 * @returns  List of collectors enabled to Tenable: ad, meid
 * As of date we only have ad and meid:
 *   - ad corresponds to send data to tenable cloud
 *   - meid is when ID360/SE features enabled
 *
 * */
export function getTenableCollectors(storeRoot: StoreRoot) {
  const collectors: Array<'ad' | 'meid'> = []

  if (
    storeRoot.stores.storeManagementDataCollection.dataCollectionStatusEntity
      ?.isEnabled
  ) {
    collectors.push('ad')
  }

  if (storeRoot.stores.storeManagementAzureAD.showADSupportStatus) {
    collectors.push('meid')
  }

  return collectors
}

/**
 *
 * @returns get deployment region
 * Parsed from the cloud-statistics siteId
 * Experimental
 *
 * */
export function getDeploymentRegion(storeRoot: StoreRoot) {
  const cloudStatistics = storeRoot.stores.storeAuthentication.cloudStatistics
  const region = cloudStatistics?.siteId?.split('-')[1]

  return region
}

/**
 * Function primarily used to track events sent from the iframes (ID360, Exposure center, landing page).
 */
export function trackEvent(storeRoot: StoreRoot) {
  return (
    eventName: string,
    eventProperties: MaybeUndef<Record<string, unknown>>
  ) => {
    if (eventName === 'page viewed') {
      storeRoot.tulApi?.logPageViewed()
      return
    }

    storeRoot.tulApi?.trackTelemetry(eventName, eventProperties)
  }
}
